import React, {useContext, useEffect, useState} from "react";
import {Card, CardBody, CardHeader, Col, Container, Row} from "reactstrap";
import {Breadcrumbs} from "../../../../../AbstractElements";
import {BootstrapTabContainer, BootstrapTabContent} from "../../../../../CommonElements/BootstrapTabs";
import ButtonIcon from "../../../../../CommonElements/ButtonIcon";
import Loader from "../../../../../Layout/Loader";
import axiosConfig from "../../../../../Config/axios";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import WalletTransactionsTable from "../../WalletTransaction/Component/WalletTransactionsTable";
import {ActivityLog} from "../../../../SystemCore/ActivityLog";
import WalletForm from "../Component/WalletForm";
import WalletDashboard from "../Component/WalletDashboard";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import type {Wallet} from "../../Types/WalletTypes";
import WalletTransactionModal from "../../WalletTransaction/Component/WalletTransactionModal";
import WalletContext from "../../Context";
import type {WalletContextType} from "../../Context/WalletContext.types";

const WalletShow = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [transactionFormVisibility, setTransactionFormVisibility] = useState<boolean>(false);
    const [transactionTypes, setTransactionTypes] = useState<string[]>([]);
    const [wallet, setWallet] = useState<Wallet | null>(null);
    const params = useParams();
    const navigate = useNavigate();
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler, hasPermission} = authContext || {};
    const {getFormattedAmount} = walletContext || {};
    const canReadWallet = !!(hasPermission && hasPermission('read-wallet'));
    const canReadWalletTransactions = !!(hasPermission && hasPermission('read-wallet-transaction'));
    const canCreateWalletTransactions = !!(hasPermission && hasPermission('create-wallet-transaction'));
    const canUpdateWallet = !!(hasPermission && hasPermission('update-wallet'));
    const canReadActivityLog = !!(hasPermission && hasPermission('read-activity-log'));
    const canDeleteWallet = !!(hasPermission && hasPermission('delete-wallet'));
    const walletRoute = getApplicationRoute('wallet', true);

    useEffect(() => {
        if (!wallet) return;
        if (wallet?.wallet_type_key === 'wallet-type-cash' && wallet?.owner_type === 'collector') {
            setTransactionTypes([
                'transaction-type-cash-transfer-collector-system',
                'transaction-type-cash-transfer-system-collector'
            ]);
        }
        if (wallet?.wallet_type_key === 'wallet-type-cash' && wallet?.owner_type === 'participant') {
            setTransactionTypes([
                'transaction-type-cash-transfer-collector-participant',
            ]);
        }
        if (wallet?.wallet_type_key === 'wallet-type-cash' && wallet?.owner_type === 'user') {
            setTransactionTypes([
                'transaction-type-cash-withdraw-system-user',
            ]);
        }
        if (wallet?.wallet_type_key === 'wallet-type-points' && wallet?.owner_type === 'participant') {
            setTransactionTypes([
                'transaction-type-cash-withdraw-system-participant',
            ]);
        }
        if (wallet?.wallet_type_key === 'wallet-type-points' && wallet?.owner_type === 'collector') {
            setTransactionTypes([
                'transaction-type-cash-withdraw-system-collector',
            ]);
        }

    }, [wallet]);

    useEffect(() => {
        if (!params.wallet) return;
        getWallet(params.wallet)
    }, [params.wallet]);

    const getWallet = (walletID: string) => {
        setLoaderVisibility(true);
        axiosConfig
            .get(`${getApplicationRoute('wallet')}/${walletID}`)
            .then((response: { data: { data: Wallet; }; }) => {
                if (response?.data?.data) {
                    setWallet(response.data.data);
                } else {
                    navigate(walletRoute);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
                navigate(walletRoute);
            });
    }
    const resolveOwnerName = (walletData: Wallet | null) => {
        return walletData?.owner?.name || '';
    };

    const resolveWalletType = (walletData: Wallet | null) => {
        return walletData?.wallet_type
            || '';
    };

    const resolveWalletTypeKey = (walletData: Wallet | null) => {
        return walletData?.wallet_type_key || walletData?.wallet_type?.key || '';
    };

    const getWalletBadgeClass = (walletTypeKey: string) => {
        if (walletTypeKey === 'wallet-type-cash') return 'badge-light-success';
        if (walletTypeKey === 'wallet-type-points') return 'badge-light-primary';
        return 'badge-light-secondary';
    };

    const walletType = resolveWalletType(wallet);
    const walletTypeKey = resolveWalletTypeKey(wallet);
    const badgeClass = getWalletBadgeClass(walletTypeKey);
    const balance = wallet?.current_balance ?? 0;
    const formatKey = walletTypeKey === 'wallet-type-cash' ? 'wallet-type-cash' : 'wallet-type-points';
    const walletTypeLabel = typeof walletType === 'string' ? walletType : '';

    return (
        <div>
            <Breadcrumbs mainTitle="Wallet" parent="Application" subParentLink={getApplicationRoute('wallet')}
                         subParent={'Wallets'} title={`Wallet #${wallet?.id || ''}`}/>
            <Container fluid={true}>
                <div className="user-profile">
                    <Loader visibility={loaderVisibility}/>
                    <Row>
                        <Col sm='12' lg='3'>
                            <Card className='hovercard text-center'>
                                <CardHeader className='social-profile'>
                                </CardHeader>
                                <div className='info'>
                                    <Row className='step3' data-intro='This is the wallet details'>
                                        <Col sm='12' lg='12' className='order-sm-0 order-xl-1'>
                                            <div className='user-designation'>
                                                <div className='title'>
                                                    <a>
                                                        {resolveOwnerName(wallet)}
                                                    </a>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm='6' lg='12' className='order-sm-1 order-xl-0'>
                                            <Row>
                                                <Col>
                                                    <div className='ttl-info text-center'>
                                                        <div className='d-flex align-items-center justify-content-center gap-2 flex-wrap'>
                                                            <span className={`badge ${badgeClass} m-r-20`}>
                                                                {walletTypeLabel || 'Unknown'}
                                                            </span>
                                                            <span>{getFormattedAmount ? getFormattedAmount(balance, formatKey, 1) : balance}</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </div>
                                <CardBody>
                                    {canDeleteWallet && (
                                        <div className='social-network theme-form'>
                                            <span className='f-w-600 text-danger'>Danger Zone</span>
                                            <ButtonIcon attrBtn={{
                                                onClick: () => navigate(walletRoute)
                                            }} type={'delete'} permission={'delete-wallet'}/>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm='12' lg='9'>
                            <Card className='hovercard'>
                                <CardBody>
                                    <BootstrapTabContainer>
                                        {canReadWallet && (
                                            <BootstrapTabContent title={'Dashboard'} icon={'icofont icofont-dashboard'}>
                                                {wallet && <WalletDashboard wallet={wallet}/>} 
                                            </BootstrapTabContent>
                                        )}
                                        {canReadWalletTransactions && (
                                            <BootstrapTabContent title={'Transactions'}
                                                                 icon={'icofont icofont-bank-transfer'}>
                                                {transactionTypes.length && canCreateWalletTransactions ?
                                                    <div className={'d-flex justify-content-between'}>
                                                        <div/>
                                                        <div className='d-flex  gap-2'>
                                                            <ButtonIcon attrBtn={{
                                                                onClick: () => {
                                                                    setTransactionFormVisibility(true)
                                                                }
                                                            }} type={'add'} permission={'create-wallet-transaction'}/>
                                                        </div>
                                                    </div>
                                                    : null}
                                                <WalletTransactionsTable params={wallet?.id ? {wallet_id: wallet.id} : {}}/>
                                            </BootstrapTabContent>
                                        )}
                                        {canUpdateWallet && (
                                            <BootstrapTabContent title={'Update'} icon={'icofont icofont-ui-edit'}>
                                                <Row className={'justify-content-center'}>
                                                    <Col md={6}>
                                                        <WalletForm wallet={wallet} callBack={(updatedWallet: Wallet) => {
                                                            setWallet(updatedWallet);
                                                        }}/>
                                                    </Col>
                                                </Row>
                                            </BootstrapTabContent>
                                        )}
                                        {canReadActivityLog && (
                                            <BootstrapTabContent title={'Activity Log'} icon={'icofont icofont-list'}>
                                                {wallet && <ActivityLog subjectType="wallet" subjectId={wallet.id}/>}
                                            </BootstrapTabContent>
                                        )}
                                    </BootstrapTabContainer>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <WalletTransactionModal
                    isOpen={transactionFormVisibility && canCreateWalletTransactions}
                    toggle={() => setTransactionFormVisibility(false)}
                    wallet_owner_id={wallet?.owner_id}
                    wallet_current_balance={wallet?.current_balance}
                    transactionTypes={transactionTypes}
                    onTransactionComplete={() => {
                        if (params.wallet) {
                            getWallet(params.wallet);
                        }
                        setTransactionFormVisibility(false);
                    }}
                />
            </Container>
        </div>
    );
};

export default WalletShow;

